import { useAuth } from '@/context/auth-context';
import { useQueryClient } from '@tanstack/react-query';
import { componentLogger } from '@/lib/logging';
import { clearAllTranslationCaches } from '@/lib/translation-loader';

/**
 * Enhanced auth hook that includes TanStack Query cache management
 * This ensures proper cache invalidation on logout to prevent cross-user data exposure
 */
export const useAuthWithCache = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const signOutWithCacheClearing = async () => {
    // Starting logout with cache clearing

    try {
      // First, clear all TanStack Query caches to prevent cross-user data exposure
      queryClient.clear();
      // TanStack Query cache cleared

      // CRITICAL FIX: Clear translation override caches to prevent cross-user data exposure
      // This removes localStorage cache that could show previous user's custom translations
      clearAllTranslationCaches();
      componentLogger.debug('auth', 'Translation override caches cleared on logout');

      // Then perform the actual logout
      await auth.signOut();
      // User signed out successfully

    } catch (_error) {
      componentLogger.error('Failed operation:', (_error as Error).message);
      // Even if logout fails, ensure ALL caches are cleared for security
      queryClient.clear();
      clearAllTranslationCaches();
      throw _error;
    }
  };

  const signInWithCacheInvalidation = async () => {
    // This function can be called after login to ensure fresh data
    // Invalidating all caches after login
    queryClient.invalidateQueries();
    // All caches invalidated after login
  };

  return {
    ...auth,
    signOut: signOutWithCacheClearing,
    signInWithCacheInvalidation,
  };
};